import type {
  AbortReasonName,
  AbortSignalWithStaticMethods,
  CreatedTimeoutSignal,
  EnrichedFetchOptions,
  MergedSignals,
  TimeoutAwareFetchOptions,
} from './types';

const NOOP = () => {};
let hasLoggedAbortSignalAnyFallbackWarning = false;

export function once(callback: () => void): () => void {
  let hasRun = false;

  return () => {
    if (hasRun) {
      return;
    }

    hasRun = true;
    callback();
  };
}

export function createAbortException(name: AbortReasonName, message: string): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException(message, name);
  }

  const error = new Error(message);
  error.name = name;

  return error;
}

function getAbortSignalStatics(): AbortSignalWithStaticMethods | null {
  if (typeof AbortSignal === 'undefined') {
    return null;
  }

  return AbortSignal as AbortSignalWithStaticMethods;
}

export function createTimeoutSignal(timeoutMs: number): CreatedTimeoutSignal {
  const abortSignalStatics = getAbortSignalStatics();

  if (abortSignalStatics?.timeout) {
    try {
      return {
        signal: abortSignalStatics.timeout(timeoutMs),
        cleanup: NOOP,
      };
    } catch (_) {
      // Fall back to AbortController + setTimeout when the static API exists
      // but throws at runtime (older engines/polyfills).
    }
  }

  if (typeof AbortController !== 'undefined') {
    const controller = new AbortController();
    const timerId = setTimeout(() => {
      controller.abort(createAbortException('TimeoutError', 'Request timed out'));
    }, timeoutMs);

    const cleanup = once(() => {
      clearTimeout(timerId);
      controller.signal.removeEventListener('abort', cleanup);
    });

    controller.signal.addEventListener('abort', cleanup, { once: true });

    return {
      signal: controller.signal,
      cleanup,
    };
  }

  return {
    signal: null,
    cleanup: NOOP,
  };
}

export function getAbortSignalAny(): ((signals: AbortSignal[]) => AbortSignal) | undefined {
  return getAbortSignalStatics()?.any;
}

export function mergeSignals(signal: AbortSignal, timeoutSignal: AbortSignal): MergedSignals {
  const controller = new AbortController();

  const onAbort = (source: AbortSignal, fallbackName: AbortReasonName) => () => {
    if (!controller.signal.aborted) {
      const reason = (source as AbortSignal & { reason?: unknown }).reason;
      controller.abort(reason ?? createAbortException(fallbackName, fallbackName));
    }
  };

  const onAbortOriginal = onAbort(signal, 'AbortError');
  const onAbortTimeout = onAbort(timeoutSignal, 'TimeoutError');

  signal.addEventListener('abort', onAbortOriginal);
  timeoutSignal.addEventListener('abort', onAbortTimeout);

  const cleanup = once(() => {
    signal.removeEventListener('abort', onAbortOriginal);
    timeoutSignal.removeEventListener('abort', onAbortTimeout);
    controller.signal.removeEventListener('abort', cleanup);
  });

  controller.signal.addEventListener('abort', cleanup, { once: true });

  return {
    signal: controller.signal,
    cleanup,
  };
}

export function withTimeoutSignal(
  fetchOptions: EnrichedFetchOptions,
  timeoutMs: number,
  debug: boolean,
): TimeoutAwareFetchOptions {
  const { timeoutMs: _timeoutMs, signal, ...rest } = fetchOptions;
  const { signal: timeoutSignal, cleanup: cleanupTimeoutSignal } = createTimeoutSignal(timeoutMs);

  if (!timeoutSignal) {
    return {
      fetchOptions: { ...rest, signal },
      cleanup: cleanupTimeoutSignal,
    };
  }

  if (!signal) {
    return {
      fetchOptions: {
        ...rest,
        signal: timeoutSignal,
      },
      cleanup: cleanupTimeoutSignal,
    };
  }

  const abortSignalAny = getAbortSignalAny();

  if (abortSignalAny) {
    return {
      fetchOptions: {
        ...rest,
        signal: abortSignalAny([signal, timeoutSignal]),
      },
      cleanup: cleanupTimeoutSignal,
    };
  }

  if (debug && !hasLoggedAbortSignalAnyFallbackWarning) {
    hasLoggedAbortSignalAnyFallbackWarning = true;
    console.warn('AbortSignal.any is not available. Falling back to manual signal merging.');
  }

  if (typeof AbortController === 'undefined') {
    return {
      fetchOptions: {
        ...rest,
        signal: timeoutSignal,
      },
      cleanup: cleanupTimeoutSignal,
    };
  }

  const { signal: mergedSignal, cleanup: cleanupMergedSignal } = mergeSignals(
    signal,
    timeoutSignal,
  );
  const cleanup = once(() => {
    cleanupMergedSignal();
    cleanupTimeoutSignal();
  });

  return {
    fetchOptions: {
      ...rest,
      signal: mergedSignal,
    },
    cleanup,
  };
}

export function resetAbortSignalAnyFallbackWarning(): void {
  hasLoggedAbortSignalAnyFallbackWarning = false;
}
